/**
 * @file     confidence.ts
 * @purpose  Per-score confidence + 95% CI on the composite score.
 *
 *           Confidence (0–1) = coverage × weighted_geomean(freshness_per_input).
 *
 *             coverage = sum(weight_i × isCovered_i) / sum(weight_i)
 *             freshness_per_input = 0.5 ^ (days_since / half_life_days)
 *             weighted_geomean: weights freshness by the rawWeight of the
 *               component the input feeds (HPI feeds hpiDeviation 15% +
 *               marketContext 10%, snapshots feed priceHistory 25%).
 *
 *           Score 95% CI is derived from the weighted standard deviation
 *           of the per-component scores, ± 1.96σ. Cheap; explains "why is
 *           this 72?" without a separate model.
 */
import type { ScoreBreakdown } from '../types';
import type { EngineComponentResult, ScoringInput } from './types';

const HALF_LIFE_HPI_DAYS = 60;
const HALF_LIFE_SNAPSHOT_DAYS = 30;

const HPI_WEIGHT_KEYS = new Set(['hpi_deviation', 'market_context']);
const SNAPSHOT_WEIGHT_KEYS = new Set(['price_history']);

export interface ConfidenceInput {
  components: Array<{ id: string; rawWeight: number; score: number | null }>;
  input: ScoringInput;
  now?: Date;
}

export interface ConfidenceDetails {
  coverage: number;
  freshness: number;
  confidence: number;
}

export function computeConfidence(args: ConfidenceInput): ConfidenceDetails {
  const now = args.now ?? new Date();
  const coverage = computeCoverage(args.components);
  const freshness = computeWeightedFreshness(args.components, args.input, now);
  const confidence = round3(coverage * freshness);
  return { coverage: round3(coverage), freshness: round3(freshness), confidence };
}

function computeCoverage(components: ConfidenceInput['components']): number {
  const total = components.reduce((s, c) => s + c.rawWeight, 0);
  if (total <= 0) return 0;
  const covered = components.filter((c) => c.score !== null).reduce((s, c) => s + c.rawWeight, 0);
  return covered / total;
}

/**
 * Weighted geometric mean of freshness factors. Each freshness factor (HPI,
 * snapshot) is raised to the share of total weight that depends on it.
 * Components with no time-anchored input contribute 1.0 (neutral) at their
 * own weight share.
 */
function computeWeightedFreshness(
  components: ConfidenceInput['components'],
  input: ScoringInput,
  now: Date,
): number {
  const totalWeight = components.reduce((s, c) => s + c.rawWeight, 0);
  if (totalWeight <= 0) return 1;

  const hpiAgeDays = input.hpi?.periodMonth ? daysBetween(input.hpi.periodMonth, now) : null;
  const snapAgeDays = lastSnapshotAge(input, now);

  const hpiFresh = hpiAgeDays != null ? decay(hpiAgeDays, HALF_LIFE_HPI_DAYS) : 1;
  const snapFresh = snapAgeDays != null ? decay(snapAgeDays, HALF_LIFE_SNAPSHOT_DAYS) : 1;

  let logSum = 0;
  for (const c of components) {
    const share = c.rawWeight / totalWeight;
    const f = HPI_WEIGHT_KEYS.has(c.id) ? hpiFresh
            : SNAPSHOT_WEIGHT_KEYS.has(c.id) ? snapFresh
            : 1;
    logSum += share * Math.log(Math.max(f, 1e-9));
  }
  return Math.exp(logSum);
}

function lastSnapshotAge(input: ScoringInput, now: Date): number | null {
  const snaps = input.priceHistory.snapshots;
  if (snaps.length === 0) return null;
  const last = snaps[snaps.length - 1];
  return daysBetween(last.snapshotDate, now);
}

function decay(days: number, halfLifeDays: number): number {
  if (days <= 0) return 1;
  return Math.pow(0.5, days / halfLifeDays);
}

function daysBetween(iso: string, now: Date): number {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, (now.getTime() - t) / 86_400_000);
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Convenience export — used by /admin/scoring. */
export function coverageFromComponents(components: EngineComponentResult[]): number {
  const total = components.reduce((s, c) => s + c.rawWeight, 0);
  if (total <= 0) return 0;
  const covered = components.filter((c) => c.score !== null).reduce((s, c) => s + c.rawWeight, 0);
  return round3(covered / total);
}

/**
 * 95% confidence interval on the composite score, derived from the weighted
 * variance of the per-component scores. Treat the composite as a weighted
 * mean of independent estimators; CI = ±1.96 × weighted σ / √n_active.
 *
 * Returns `null` when there are fewer than 2 non-null components (CI not
 * defined). Bounds clamp to [0, 100] since the underlying score does.
 */
export interface ScoreInterval {
  low: number;
  high: number;
  marginPct: number;
}

export function computeScoreInterval(
  components: Array<{ rawWeight: number; score: number | null }>,
  centerScore: number,
): ScoreInterval | null {
  const active = components.filter((c) => c.score !== null) as Array<{ rawWeight: number; score: number }>;
  if (active.length < 2) return null;
  const totalW = active.reduce((s, c) => s + c.rawWeight, 0);
  if (totalW <= 0) return null;
  const mean = active.reduce((s, c) => s + c.score * c.rawWeight, 0) / totalW;
  const variance = active.reduce((s, c) => s + c.rawWeight * (c.score - mean) ** 2, 0) / totalW;
  const sigma = Math.sqrt(variance);
  const margin = 1.96 * sigma / Math.sqrt(active.length);
  return {
    low: Math.max(0, Math.round((centerScore - margin) * 10) / 10),
    high: Math.min(100, Math.round((centerScore + margin) * 10) / 10),
    marginPct: Math.round((margin / Math.max(centerScore, 1)) * 1000) / 10,
  };
}
